import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import {
  Plus, Search, FileSpreadsheet, Euro, Trash2, Printer, Edit2,
  ClipboardCheck, UserPlus, Stethoscope, Eye, Clock, CheckCircle, XCircle, AlertCircle, FileSignature, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import DictableTextarea from '@/components/DictableTextarea';
import { openPrintWindow } from '@/lib/letterhead';
import PreventivoFormBody, { SERVIZI_DISPONIBILI } from '@/components/PreventivoFormBody';
import DatePickerField from '@/components/DatePickerField';

const prevStatoMap = {
  bozza: { label: 'Bozza', class: 'bg-slate-100 text-slate-600' },
  inviato: { label: 'Inviato', class: 'bg-blue-100 text-blue-700' },
  accettato: { label: 'Accettato', class: 'bg-emerald-100 text-emerald-700' },
  convertito: { label: 'Convertito in Ospite', class: 'bg-indigo-100 text-indigo-700' },
  rifiutato: { label: 'Rifiutato', class: 'bg-red-100 text-red-700' },
  scaduto: { label: 'Scaduto', class: 'bg-amber-100 text-amber-700' },
};

const laStatoMap = {
  in_attesa: { label: 'In Attesa', class: 'bg-amber-100 text-amber-700', icon: Clock },
  contattato: { label: 'Contattato', class: 'bg-blue-100 text-blue-700', icon: Clock },
  valutato_idoneo: { label: 'Idoneo', class: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  valutato_non_idoneo: { label: 'Non Idoneo', class: 'bg-red-100 text-red-700', icon: XCircle },
  valutato_idoneo_riserva: { label: 'Idoneo con Riserva', class: 'bg-cyan-100 text-cyan-700', icon: AlertCircle },
  da_rivalutare: { label: 'Da Rivalutare', class: 'bg-purple-100 text-purple-700', icon: AlertCircle },
  ammesso: { label: 'Ammesso', class: 'bg-green-100 text-green-800', icon: CheckCircle },
  rinunciato: { label: 'Rinunciato', class: 'bg-slate-100 text-slate-500', icon: XCircle },
};

const prioritaMap = {
  urgente: { label: 'Urgente', class: 'bg-red-100 text-red-700' },
  alta: { label: 'Alta', class: 'bg-orange-100 text-orange-700' },
  media: { label: 'Media', class: 'bg-amber-100 text-amber-700' },
  bassa: { label: 'Bassa', class: 'bg-slate-100 text-slate-600' },
};

const tipAssistenza = {
  non_autosufficiente: 'Non Autosufficiente',
  parzialmente_autosufficiente: 'Parzialmente Autosufficiente',
  autosufficiente: 'Autosufficiente',
  demenza: 'Demenza',
  sollievo: 'Ricovero di Sollievo',
};

const soggiornoLabels = { temporaneo: 'Temporaneo', permanente: 'Permanente', sollievo: 'Sollievo' };
const cameraLabels = { singola: 'Singola', doppia: 'Doppia' };

export default function Preventivi() {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('preventivi');
  // Preventivi state
  const [prevItems, setPrevItems] = useState([]);
  const [prevSearch, setPrevSearch] = useState('');
  const [prevFiltroStato, setPrevFiltroStato] = useState('tutti');
  const [prevLoading, setPrevLoading] = useState(true);
  const [showPrevForm, setShowPrevForm] = useState(false);
  const [editingPrev, setEditingPrev] = useState(null);

  // Lista attesa state
  const [laItems, setLaItems] = useState([]);
  const [laSearch, setLaSearch] = useState('');
  const [laFiltroStato, setLaFiltroStato] = useState('tutti');
  const [laFiltroPriorita, setLaFiltroPriorita] = useState('tutti');
  const [laLoading, setLaLoading] = useState(true);
  const [showLaForm, setShowLaForm] = useState(false);
  const [laForm, setLaForm] = useState({
    nome: '', cognome: '', data_nascita: '', codice_fiscale: '', telefono: '', email: '',
    referente_nome: '', referente_telefono: '', data_richiesta: new Date().toISOString().slice(0, 10),
    priorita: 'media', tipologia_assistenza: 'non_autosufficiente', provenienza: '', motivo_richiesta: '', note: ''
  });

  // Valutazione state
  const [showValutazione, setShowValutazione] = useState(false);
  const [showDettaglio, setShowDettaglio] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [valutazioni, setValutazioni] = useState([]);
  // Ammissione state
  const [showAmmissione, setShowAmmissione] = useState(false);
  const [ammItem, setAmmItem] = useState(null);
  const [ammForm, setAmmForm] = useState({ stanza: '', letto: '', area: '', data_ingresso: new Date().toISOString().slice(0, 10), mmg_nome: '' });
  const [valForm, setValForm] = useState({
    diagnosi_principali: '', livello_autonomia: 'parzialmente_dipendente', stato_cognitivo: 'integro',
    disturbi_comportamentali: '', necessita_infermieristiche: '',
    barthel_index: '', mmse: '', braden: '', tinetti: '',
    situazione_familiare: '', situazione_economica: '', servizi_attivi: '',
    esito: 'idoneo', motivazione_esito: '', note: '',
    data_valutazione: new Date().toISOString().slice(0, 10), equipe_valutante: ''
  });

  // Ammissioni recenti state
  const [ammRecenti, setAmmRecenti] = useState([]);
  const [ammRecLoading, setAmmRecLoading] = useState(true);

  // == FETCH ==
  const fetchPreventivi = useCallback(() => {
    const params = {};
    if (prevSearch) params.search = prevSearch;
    if (prevFiltroStato !== 'tutti') params.stato = prevFiltroStato;
    setPrevLoading(true);
    api.get('/preventivi', { params }).then(r => setPrevItems(r.data)).catch(() => {}).finally(() => setPrevLoading(false));
  }, [prevSearch, prevFiltroStato]);

  const fetchListaAttesa = useCallback(() => {
    const params = {};
    if (laSearch) params.search = laSearch;
    if (laFiltroStato !== 'tutti') params.stato = laFiltroStato;
    if (laFiltroPriorita !== 'tutti') params.priorita = laFiltroPriorita;
    setLaLoading(true);
    api.get('/lista-attesa', { params }).then(r => setLaItems(r.data)).catch(() => {}).finally(() => setLaLoading(false));
  }, [laSearch, laFiltroStato, laFiltroPriorita]);

  const fetchAmmRecenti = useCallback(() => {
    setAmmRecLoading(true);
    api.get('/ammissioni-recenti').then(r => setAmmRecenti(r.data)).catch(() => {}).finally(() => setAmmRecLoading(false));
  }, []);

  useEffect(() => { fetchPreventivi(); }, [fetchPreventivi]);
  useEffect(() => { fetchListaAttesa(); }, [fetchListaAttesa]);
  useEffect(() => { fetchAmmRecenti(); }, [fetchAmmRecenti]);

  // == Preventivi HANDLERS ==
  const openEditPrev = (p) => {
    setEditingPrev(p);
    setShowPrevForm(true);
  };

  const handleSavePrev = useCallback(async (formData) => {
    if (!formData.nome_candidato || !formData.cognome_candidato) { toast.error('Inserisci nome e cognome'); return; }
    const payload = {
      ...formData,
      retta_mensile_base: parseFloat(formData.retta_mensile_base),
      durata_prevista_giorni: parseInt(formData.durata_prevista_giorni) || 30,
      servizi_accessori: formData.servizi_accessori,
    };
    try {
      if (editingPrev) {
        await api.put(`/preventivi/${editingPrev.id}`, payload);
        toast.success('Preventivo aggiornato');
      } else {
        await api.post('/preventivi', payload);
        toast.success('Preventivo creato');
      }
      setShowPrevForm(false);
      setEditingPrev(null);
      fetchPreventivi();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  }, [editingPrev, fetchPreventivi]);

  const handleCancelPrev = useCallback(() => {
    setShowPrevForm(false);
    setEditingPrev(null);
  }, []);

  const handleUpdatePrevStato = async (id, stato) => {
    try {
      await api.put(`/preventivi/${id}`, { stato });
      if (stato === 'accettato') {
        toast.success('Preventivo accettato! Candidato spostato automaticamente in Lista d\'Attesa', { duration: 4000 });
        fetchListaAttesa();
      } else {
        toast.success('Stato aggiornato');
      }
      fetchPreventivi();
    } catch { toast.error('Errore'); }
  };

  const handleDeletePrev = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo preventivo?')) return;
    try { await api.delete(`/preventivi/${id}`); toast.success('Eliminato'); fetchPreventivi(); } catch { toast.error('Errore'); }
  };

  const handlePrintPrev = (p) => {
    const sogg = soggiornoLabels[p.tipologia_soggiorno] || p.tipologia_soggiorno;
    const cam = cameraLabels[p.tipologia_camera] || p.tipologia_camera;
    const serviziAttivi = (p.servizi_accessori || []).filter(s => s.attivo);
    const rettaBase = p.retta_mensile_base || p.retta_mensile || (p.retta_giornaliera ? p.retta_giornaliera * 30 : 0);
    const serviziTotMese = serviziAttivi.reduce((sum, s) => sum + (parseFloat(s.prezzo) || 0), 0);
    const rettaTotale = rettaBase + serviziTotMese;

    let body = `<div class="info-grid">`;
    body += `<div class="info-item"><span class="info-label">Candidato</span><span class="info-value">${p.cognome_candidato} ${p.nome_candidato}</span></div>`;
    if (p.codice_fiscale) body += `<div class="info-item"><span class="info-label">Codice Fiscale</span><span class="info-value" style="font-family:monospace">${p.codice_fiscale}</span></div>`;
    if (p.telefono) body += `<div class="info-item"><span class="info-label">Telefono</span><span class="info-value">${p.telefono}</span></div>`;
    if (p.email) body += `<div class="info-item"><span class="info-label">Email</span><span class="info-value">${p.email}</span></div>`;
    if (p.referente_nome) body += `<div class="info-item"><span class="info-label">Referente</span><span class="info-value">${p.referente_nome} ${p.referente_telefono ? '(' + p.referente_telefono + ')' : ''}</span></div>`;
    body += `</div>`;

    body += `<p class="section-title">Dettaglio Preventivo n. ${p.numero}</p>`;
    body += `<div class="info-grid">`;
    body += `<div class="info-item"><span class="info-label">Soggiorno</span><span class="info-value">${sogg}</span></div>`;
    body += `<div class="info-item"><span class="info-label">Camera</span><span class="info-value">${cam}</span></div>`;
    if (p.area_richiesta) body += `<div class="info-item"><span class="info-label">Area</span><span class="info-value">${p.area_richiesta}</span></div>`;
    body += `<div class="info-item"><span class="info-label">Retta Mensile Base</span><span class="info-value">EUR ${rettaBase.toFixed(2)}</span></div>`;
    body += `<div class="info-item"><span class="info-label">Durata Prevista</span><span class="info-value">${p.durata_prevista_giorni} giorni</span></div>`;
    body += `</div>`;

    if (serviziAttivi.length > 0) {
      body += `<p class="section-title">Servizi Accessori</p>`;
      body += `<table><thead><tr><th>Servizio</th><th>Costo Mensile</th></tr></thead><tbody>`;
      serviziAttivi.forEach(s => { body += `<tr><td>${s.nome}</td><td>EUR ${parseFloat(s.prezzo).toFixed(2)}</td></tr>`; });
      body += `<tr style="font-weight:bold"><td>Totale servizi/mese</td><td>EUR ${serviziTotMese.toFixed(2)}</td></tr>`;
      body += `</tbody></table>`;
    }

    body += `<div style="margin:16px 0;padding:12px;border:2px solid #15803d;border-radius:8px;text-align:center">`;
    body += `<p style="font-size:10px;color:#64748b;margin-bottom:4px">RETTA MENSILE COMPLESSIVA</p>`;
    body += `<p style="font-size:22px;font-weight:700;color:#15803d">EUR ${rettaTotale.toFixed(2)}</p>`;
    body += `<p style="font-size:9px;color:#64748b;margin-top:4px">Retta base EUR ${rettaBase.toFixed(2)} + Servizi EUR ${serviziTotMese.toFixed(2)}/mese</p>`;
    body += `</div>`;

    body += `<p class="section-title">Servizi Inclusi nella Retta</p>`;
    body += `<ul style="padding-left:20px;font-size:10px;line-height:1.8"><li>Vitto e alloggio in camera ${cam.toLowerCase()}</li><li>Assistenza socio-assistenziale diurna e notturna</li><li>Assistenza infermieristica</li><li>Igiene personale e vestizione</li><li>Somministrazione pasti</li><li>Attivita di animazione e socializzazione</li><li>Lavanderia biancheria piana</li></ul>`;

    body += `<p class="section-title">Esclusioni dalla Retta</p>`;
    body += `<ul style="padding-left:20px;font-size:10px;line-height:1.8"><li>Tickets sanitari (salvo esenzione)</li><li>Spese per barbiere/parrucchiera (se non incluso nei servizi)</li><li>Spese di carattere personale</li></ul>`;

    if (p.note) body += `<p class="section-title">Note</p><p style="font-size:10px">${p.note}</p>`;

    body += `<p style="font-size:9px;color:#64748b;margin-top:20px">Preventivo valido 30 giorni dalla data di emissione. Ai sensi del D.Lgs. 196/2003 e del GDPR (Reg. UE 2016/679).</p>`;
    body += `<div class="signature-row"><div class="signature-block">Timbro e Firma della Struttura</div><div class="signature-block">Firma del Richiedente</div></div>`;

    openPrintWindow(`Preventivo di Ospitalita n. ${p.numero}`, body, {
      subtitle: `${p.cognome_candidato} ${p.nome_candidato}`,
      date: new Date(p.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })
    });
  };

  // == Lista Attesa HANDLERS ==
  const handleCreateLa = async () => {
    if (!laForm.nome || !laForm.cognome || !laForm.telefono) { toast.error('Inserisci nome, cognome e telefono'); return; }
    try {
      await api.post('/lista-attesa', laForm);
      toast.success('Candidato aggiunto alla lista');
      setShowLaForm(false);
      setLaForm({ nome: '', cognome: '', data_nascita: '', codice_fiscale: '', telefono: '', email: '', referente_nome: '', referente_telefono: '', data_richiesta: new Date().toISOString().slice(0, 10), priorita: 'media', tipologia_assistenza: 'non_autosufficiente', provenienza: '', motivo_richiesta: '', note: '' });
      fetchListaAttesa();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const handleUpdateLaStato = async (id, stato) => {
    try { await api.put(`/lista-attesa/${id}`, { stato }); toast.success('Stato aggiornato'); fetchListaAttesa(); } catch { toast.error('Errore'); }
  };

  const handleAnnullaAmmissione = async (id) => {
    if (!window.confirm('Sei sicuro di voler annullare l\'ammissione? L\'ospite creato verra eliminato se non ha dati clinici.')) return;
    try {
      const res = await api.post(`/lista-attesa/${id}/annulla-ammissione`);
      toast.success(res.data.message || 'Ammissione annullata');
      fetchListaAttesa();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore annullamento');
    }
  };

  const handleDeleteLa = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo candidato dalla lista d\'attesa?')) return;
    try {
      await api.delete(`/lista-attesa/${id}`);
      toast.success('Candidato eliminato dalla lista');
      fetchListaAttesa();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const openValutazione = (item) => {
    setSelectedItem(item);
    setValForm(f => ({ ...f, diagnosi_principali: '', livello_autonomia: 'parzialmente_dipendente', stato_cognitivo: 'integro', esito: 'idoneo', data_valutazione: new Date().toISOString().slice(0, 10) }));
    setShowValutazione(true);
  };

  const handleCreateValutazione = async () => {
    if (!valForm.diagnosi_principali) { toast.error('Inserisci le diagnosi principali'); return; }
    try {
      await api.post('/valutazioni-idoneita', {
        ...valForm, candidato_id: selectedItem.id,
        barthel_index: valForm.barthel_index ? parseInt(valForm.barthel_index) : null,
        mmse: valForm.mmse ? parseInt(valForm.mmse) : null,
        braden: valForm.braden ? parseInt(valForm.braden) : null,
        tinetti: valForm.tinetti ? parseInt(valForm.tinetti) : null,
      });
      toast.success('Valutazione registrata');
      setShowValutazione(false);
      fetchListaAttesa();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const openDettaglio = async (item) => {
    setSelectedItem(item);
    try { const res = await api.get('/valutazioni-idoneita', { params: { candidato_id: item.id } }); setValutazioni(res.data); } catch { setValutazioni([]); }
    setShowDettaglio(true);
  };

  const openAmmissione = (item) => {
    setAmmItem(item);
    setAmmForm({ stanza: '', letto: '', area: item.area_richiesta || '', data_ingresso: new Date().toISOString().slice(0, 10), mmg_nome: '' });
    setShowAmmissione(true);
  };

  const handleAmmetti = async () => {
    if (!ammForm.stanza) { toast.error('Inserisci la stanza'); return; }
    try {
      const res = await api.post(`/lista-attesa/${ammItem.id}/ammetti`, ammForm);
      toast.success(`${res.data.cognome} ${res.data.nome} ammesso/a! Anagrafica creata.`, { duration: 5000 });
      setShowAmmissione(false);
      fetchListaAttesa();
      fetchPreventivi();
      fetchAmmRecenti();
      navigate(`/ospiti/${res.data.ospite_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore ammissione'); }
  };

  const inAttesa = laItems.filter(i => i.stato === 'in_attesa').length;
  const valutati = laItems.filter(i => i.stato?.startsWith('valutato')).length;

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto" data-testid="ammissioni-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-[Cinzel] tracking-tight">Ammissioni</h1>
          <p className="text-sm text-slate-500 mt-0.5">Gestione preventivi, candidature e lista d'attesa</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="grid w-full max-w-lg grid-cols-3">
          <TabsTrigger value="preventivi" data-testid="tab-preventivi" className="flex items-center gap-1.5">
            <FileSpreadsheet className="w-3.5 h-3.5" />Preventivi
            <Badge variant="secondary" className="text-[10px] ml-1">{prevItems.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="lista-attesa" data-testid="tab-lista-attesa" className="flex items-center gap-1.5">
            <ClipboardCheck className="w-3.5 h-3.5" />Lista Attesa
            <Badge variant="secondary" className="text-[10px] ml-1">{laItems.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="ammissioni" data-testid="tab-ammissioni" className="flex items-center gap-1.5">
            <CheckCircle className="w-3.5 h-3.5" />Ammissioni
            <Badge variant="secondary" className="text-[10px] ml-1">{ammRecenti.length}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* ===== PREVENTIVI TAB ===== */}
        <TabsContent value="preventivi" className="space-y-4 mt-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 flex-1 flex-wrap">
              <div className="relative min-w-[240px] max-w-md flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input data-testid="search-preventivi" placeholder="Cerca candidato..." className="pl-9 h-10" value={prevSearch} onChange={e => setPrevSearch(e.target.value)} />
              </div>
              <Select value={prevFiltroStato} onValueChange={setPrevFiltroStato}>
                <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="tutti">Tutti gli stati</SelectItem>{Object.entries(prevStatoMap).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {hasRole('coordinatore', 'amministrazione') && (
              <Button data-testid="add-preventivo-btn" onClick={() => { setEditingPrev(null); setShowPrevForm(true); }} className="bg-brand hover:bg-brand-dark text-white">
                <Plus className="w-4 h-4 mr-2" />Nuovo Preventivo
              </Button>
            )}
          </div>

          {prevLoading ? (
            <div className="space-y-3">{[...Array(3)].map((_, i) => <Card key={i} className="animate-pulse"><CardContent className="h-16 p-4" /></Card>)}</div>
          ) : prevItems.length === 0 ? (
            <div className="text-center py-16"><FileSpreadsheet className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">Nessun preventivo</p></div>
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">N. Preventivo</TableHead>
                      <TableHead className="text-xs">Candidato</TableHead>
                      <TableHead className="text-xs">Soggiorno</TableHead>
                      <TableHead className="text-xs">Camera</TableHead>
                      <TableHead className="text-xs">Retta/mese</TableHead>
                      <TableHead className="text-xs">Stato</TableHead>
                      <TableHead className="text-xs">Data</TableHead>
                      <TableHead className="text-xs">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {prevItems.map(p => {
                      const s = prevStatoMap[p.stato] || prevStatoMap.bozza;
                      return (
                        <TableRow key={p.id} data-testid={`preventivo-row-${p.id}`}>
                          <TableCell className="text-xs font-mono">{p.numero}</TableCell>
                          <TableCell>
                            <p className="text-sm font-medium">{p.cognome_candidato} {p.nome_candidato}</p>
                            {p.telefono && <p className="text-xs text-slate-400">{p.telefono}</p>}
                          </TableCell>
                          <TableCell className="text-sm">{soggiornoLabels[p.tipologia_soggiorno] || p.tipologia_soggiorno}</TableCell>
                          <TableCell className="text-sm">{cameraLabels[p.tipologia_camera] || p.tipologia_camera}</TableCell>
                          <TableCell className="text-sm font-bold text-emerald-700"><Euro className="w-3 h-3 inline mr-0.5" />{(p.retta_mensile || p.retta_mensile_base || 0).toFixed(2)}</TableCell>
                          <TableCell>
                            <Badge className={`text-[10px] ${s.class}`}>{s.label}</Badge>
                            {p.stato === 'accettato' && p.lista_attesa_id && <span className="text-[9px] text-blue-500 block mt-0.5">In lista d'attesa</span>}
                            {p.stato === 'convertito' && p.ospite_id && <Button variant="link" size="sm" className="h-4 text-[9px] text-indigo-600 px-0" onClick={() => navigate(`/ospiti/${p.ospite_id}`)}>Vai a scheda →</Button>}
                          </TableCell>
                          <TableCell className="text-xs text-slate-400">{new Date(p.created_at).toLocaleDateString('it-IT')}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {hasRole('coordinatore', 'amministrazione') && (
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEditPrev(p)} data-testid={`edit-preventivo-${p.id}`} title="Modifica">
                                  <Edit2 className="w-3.5 h-3.5 text-blue-500" />
                                </Button>
                              )}
                              {p.stato === 'bozza' && <Button variant="ghost" size="sm" className="h-7 text-xs text-brand" onClick={() => handleUpdatePrevStato(p.id, 'inviato')}>Invia</Button>}
                              {p.stato === 'inviato' && <>
                                <Button variant="ghost" size="sm" className="h-7 text-xs text-emerald-600" onClick={() => handleUpdatePrevStato(p.id, 'accettato')}>Accetta</Button>
                                <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600" onClick={() => handleUpdatePrevStato(p.id, 'rifiutato')}>Rifiuta</Button>
                              </>}
                              {p.stato === 'accettato' && hasRole('coordinatore', 'amministrazione') && (
                                <Button variant="ghost" size="sm" className="h-7 text-xs text-purple-600 font-medium" onClick={() => navigate(`/preventivi/contratto/${p.id}`)} data-testid={`genera-contratto-${p.id}`} title="Genera contratto da questo preventivo">
                                  <FileSignature className="w-3.5 h-3.5 mr-1" />Contratto
                                </Button>
                              )}
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handlePrintPrev(p)} data-testid={`print-preventivo-${p.id}`} title="Stampa"><Printer className="w-3.5 h-3.5 text-slate-500" /></Button>
                              {hasRole('coordinatore', 'amministrazione') && <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDeletePrev(p.id)} title="Elimina"><Trash2 className="w-3.5 h-3.5 text-slate-400" /></Button>}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ===== LISTA ATTESA TAB ===== */}
        <TabsContent value="lista-attesa" className="space-y-4 mt-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 flex-1 flex-wrap">
              <div className="relative min-w-[200px] max-w-sm flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Cerca candidato..." className="pl-9 h-10" value={laSearch} onChange={e => setLaSearch(e.target.value)} />
              </div>
              <Select value={laFiltroStato} onValueChange={setLaFiltroStato}>
                <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="tutti">Tutti gli stati</SelectItem>{Object.entries(laStatoMap).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={laFiltroPriorita} onValueChange={setLaFiltroPriorita}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="tutti">Tutte le priorita</SelectItem>{Object.entries(prioritaMap).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {hasRole('coordinatore', 'amministrazione') && (
              <Button data-testid="add-candidato-btn" onClick={() => setShowLaForm(true)} className="bg-brand hover:bg-brand-dark text-white">
                <UserPlus className="w-4 h-4 mr-2" />Nuovo Candidato
              </Button>
            )}
          </div>

          <p className="text-xs text-slate-500">{laItems.length} candidati ({inAttesa} in attesa, {valutati} valutati)</p>

          {laLoading ? (
            <div className="space-y-3">{[...Array(3)].map((_, i) => <Card key={i} className="animate-pulse"><CardContent className="h-20 p-4" /></Card>)}</div>
          ) : laItems.length === 0 ? (
            <div className="text-center py-16"><ClipboardCheck className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">Nessun candidato in lista</p></div>
          ) : (
            <div className="space-y-3" data-testid="lista-attesa-items">
              {laItems.map((item, idx) => {
                const s = laStatoMap[item.stato] || laStatoMap.in_attesa;
                const pr = prioritaMap[item.priorita] || prioritaMap.media;
                return (
                  <Card key={item.id} className="hover:shadow-md transition-shadow" data-testid={`la-item-${item.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600">{item.posizione || idx + 1}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-semibold text-slate-900">{item.cognome} {item.nome}</p>
                            <Badge className={`text-[10px] ${s.class}`}>{s.label}</Badge>
                            <Badge className={`text-[10px] ${pr.class}`}>{pr.label}</Badge>
                            <Badge variant="outline" className="text-[10px]">{tipAssistenza[item.tipologia_assistenza] || item.tipologia_assistenza}</Badge>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                            {item.telefono && <span>{item.telefono}</span>}
                            {item.data_richiesta && <span>Richiesta: {new Date(item.data_richiesta).toLocaleDateString('it-IT')}</span>}
                            {item.provenienza && <span>Da: {item.provenienza}</span>}
                            {item.preventivo_numero && <span className="text-blue-500">Prev. {item.preventivo_numero}</span>}
                            {item.retta_mensile > 0 && <span className="text-emerald-600 font-medium">EUR {item.retta_mensile.toFixed(2)}/mese</span>}
                          </div>
                          {item.stato === 'ammesso' && item.ospite_id && (
                            <Button variant="link" size="sm" className="h-5 text-xs text-brand px-0 mt-0.5" onClick={() => navigate(`/ospiti/${item.ospite_id}`)}>
                              Vai alla scheda ospite →
                            </Button>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => openDettaglio(item)} data-testid={`la-dettaglio-${item.id}`}><Eye className="w-3.5 h-3.5 mr-1" />Dettaglio</Button>
                          {hasRole('coordinatore', 'medico') && item.stato === 'in_attesa' && (
                            <>
                              <Button variant="ghost" size="sm" className="h-8 text-xs text-brand" onClick={() => handleUpdateLaStato(item.id, 'contattato')}>Contattato</Button>
                              <Button variant="outline" size="sm" className="h-8 text-xs text-emerald-700 border-emerald-200" onClick={() => openValutazione(item)} data-testid={`la-valuta-${item.id}`}><Stethoscope className="w-3.5 h-3.5 mr-1" />Valuta</Button>
                            </>
                          )}
                          {hasRole('coordinatore') && (item.stato === 'valutato_idoneo' || item.stato === 'valutato_idoneo_riserva' || item.stato === 'in_attesa') && (
                            <Button variant="outline" size="sm" className="h-8 text-xs text-green-700 border-green-200 font-medium" onClick={() => openAmmissione(item)} data-testid={`la-ammetti-${item.id}`}>
                              <UserPlus className="w-3.5 h-3.5 mr-1" />Ammetti
                            </Button>
                          )}
                          {hasRole('coordinatore') && item.stato !== 'rinunciato' && item.stato !== 'ammesso' && (
                            <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-400" onClick={() => handleUpdateLaStato(item.id, 'rinunciato')} data-testid={`la-rinuncia-${item.id}`}>Rinuncia</Button>
                          )}
                          {hasRole('coordinatore') && item.stato === 'rinunciato' && (
                            <Button variant="outline" size="sm" className="h-8 text-xs text-brand border-blue-200" onClick={() => handleUpdateLaStato(item.id, 'in_attesa')} data-testid={`la-ripristina-${item.id}`}>
                              <RotateCcw className="w-3.5 h-3.5 mr-1" />Ripristina in Lista
                            </Button>
                          )}
                          {hasRole('coordinatore') && item.stato === 'ammesso' && (
                            <Button variant="outline" size="sm" className="h-8 text-xs text-red-600 border-red-200" onClick={() => handleAnnullaAmmissione(item.id)} data-testid={`la-annulla-ammissione-${item.id}`}>
                              <RotateCcw className="w-3.5 h-3.5 mr-1" />Annulla Ammissione
                            </Button>
                          )}
                          {hasRole('coordinatore', 'amministrazione') && item.stato !== 'ammesso' && (
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-red-600" onClick={() => handleDeleteLa(item.id)} data-testid={`la-delete-${item.id}`} title="Elimina dalla lista">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ===== AMMISSIONI RECENTI TAB ===== */}
        <TabsContent value="ammissioni" className="space-y-4 mt-4">
          {ammRecLoading ? (
            <div className="space-y-3">{[...Array(3)].map((_, i) => <Card key={i} className="animate-pulse"><CardContent className="h-16 p-4" /></Card>)}</div>
          ) : ammRecenti.length === 0 ? (
            <div className="text-center py-16"><CheckCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">Nessuna ammissione recente</p></div>
          ) : (
            <div className="space-y-2" data-testid="ammissioni-recenti">
              {ammRecenti.map(item => (
                <Card key={item.id} className="hover:shadow-sm transition-shadow">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{item.cognome} {item.nome}</p>
                      <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5">
                        {item.data_ammissione && <span>Ammesso: {new Date(item.data_ammissione).toLocaleDateString('it-IT')}</span>}
                        {item.tipologia_assistenza && <span>{tipAssistenza[item.tipologia_assistenza] || item.tipologia_assistenza}</span>}
                        {item.preventivo_numero && <span className="text-blue-500">Prev. {item.preventivo_numero}</span>}
                        {item.retta_mensile > 0 && <span className="text-emerald-600 font-medium">EUR {item.retta_mensile.toFixed(2)}/mese</span>}
                      </div>
                    </div>
                    {item.ospite_id && (
                      <Button variant="outline" size="sm" className="text-xs text-brand border-blue-200" onClick={() => navigate(`/ospiti/${item.ospite_id}`)}>
                        <Eye className="w-3.5 h-3.5 mr-1" />Scheda Ospite
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* == PREVENTIVO FORM DIALOG == */}
      <Dialog open={showPrevForm} onOpenChange={(v) => { if (!v) handleCancelPrev(); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle>{editingPrev ? 'Modifica Preventivo' : 'Nuovo Preventivo'}</DialogTitle></DialogHeader>
          <PreventivoFormBody
            editingPrev={editingPrev}
            onSave={handleSavePrev}
            onCancel={handleCancelPrev}
          />
        </DialogContent>
      </Dialog>

      {/* == LISTA ATTESA FORM DIALOG == */}
      <Dialog open={showLaForm} onOpenChange={setShowLaForm}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Inserimento Rapido in Lista di Attesa</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-400">Per candidature con preventivo, usa il tab Preventivi. Questo form e per inserimenti diretti.</p>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Nome *</Label><Input data-testid="la-nome" value={laForm.nome} onChange={e => setLaForm(f => ({ ...f, nome: e.target.value }))} /></div>
              <div><Label className="text-xs">Cognome *</Label><Input data-testid="la-cognome" value={laForm.cognome} onChange={e => setLaForm(f => ({ ...f, cognome: e.target.value }))} /></div>
              <div><Label className="text-xs">Telefono *</Label><Input data-testid="la-telefono" value={laForm.telefono} onChange={e => setLaForm(f => ({ ...f, telefono: e.target.value }))} /></div>
              <div><Label className="text-xs">Referente</Label><Input value={laForm.referente_nome} onChange={e => setLaForm(f => ({ ...f, referente_nome: e.target.value }))} /></div>
            </div>
            <Separator />
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Priorita</Label>
                <Select value={laForm.priorita} onValueChange={v => setLaForm(f => ({ ...f, priorita: v }))}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="urgente">Urgente</SelectItem><SelectItem value="alta">Alta</SelectItem><SelectItem value="media">Media</SelectItem><SelectItem value="bassa">Bassa</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs">Tipologia Assistenza</Label>
                <Select value={laForm.tipologia_assistenza} onValueChange={v => setLaForm(f => ({ ...f, tipologia_assistenza: v }))}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(tipAssistenza).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div><Label className="text-xs">Motivo Richiesta</Label><DictableTextarea value={laForm.motivo_richiesta} onChange={e => setLaForm(f => ({ ...f, motivo_richiesta: e.target.value }))} rows={2} /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setShowLaForm(false)}>Annulla</Button><Button data-testid="save-candidato" onClick={handleCreateLa} className="bg-brand hover:bg-brand-dark text-white">Inserisci</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* == VALUTAZIONE IDONEITA DIALOG == */}
      <Dialog open={showValutazione} onOpenChange={setShowValutazione}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Stethoscope className="w-5 h-5 text-emerald-600" />Valutazione Idoneita - {selectedItem?.cognome} {selectedItem?.nome}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Valutazione Clinica</p>
            <div><Label className="text-xs">Diagnosi Principali *</Label><DictableTextarea data-testid="val-diagnosi" value={valForm.diagnosi_principali} onChange={e => setValForm(f => ({ ...f, diagnosi_principali: e.target.value }))} rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Livello Autonomia</Label>
                <Select value={valForm.livello_autonomia} onValueChange={v => setValForm(f => ({ ...f, livello_autonomia: v }))}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="autonomo">Autonomo</SelectItem><SelectItem value="parzialmente_dipendente">Parzialmente Dipendente</SelectItem><SelectItem value="totalmente_dipendente">Totalmente Dipendente</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs">Stato Cognitivo</Label>
                <Select value={valForm.stato_cognitivo} onValueChange={v => setValForm(f => ({ ...f, stato_cognitivo: v }))}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="integro">Integro</SelectItem><SelectItem value="lieve_decadimento">Lieve Decadimento</SelectItem><SelectItem value="moderato_decadimento">Moderato Decadimento</SelectItem><SelectItem value="grave_decadimento">Grave Decadimento</SelectItem></SelectContent></Select></div>
            </div>
            <div><Label className="text-xs">Disturbi Comportamentali</Label><DictableTextarea value={valForm.disturbi_comportamentali} onChange={e => setValForm(f => ({ ...f, disturbi_comportamentali: e.target.value }))} rows={2} /></div>
            <div><Label className="text-xs">Necessita Infermieristiche</Label><DictableTextarea value={valForm.necessita_infermieristiche} onChange={e => setValForm(f => ({ ...f, necessita_infermieristiche: e.target.value }))} rows={2} /></div>
            <Separator />
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Scale di Valutazione</p>
            <div className="grid grid-cols-4 gap-3">
              <div><Label className="text-xs">Barthel (0-100)</Label><Input type="number" min="0" max="100" value={valForm.barthel_index} onChange={e => setValForm(f => ({ ...f, barthel_index: e.target.value }))} /></div>
              <div><Label className="text-xs">MMSE (0-30)</Label><Input type="number" min="0" max="30" value={valForm.mmse} onChange={e => setValForm(f => ({ ...f, mmse: e.target.value }))} /></div>
              <div><Label className="text-xs">Braden (6-23)</Label><Input type="number" min="6" max="23" value={valForm.braden} onChange={e => setValForm(f => ({ ...f, braden: e.target.value }))} /></div>
              <div><Label className="text-xs">Tinetti (0-28)</Label><Input type="number" min="0" max="28" value={valForm.tinetti} onChange={e => setValForm(f => ({ ...f, tinetti: e.target.value }))} /></div>
            </div>
            <Separator />
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Valutazione Sociale</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Situazione Familiare</Label><DictableTextarea value={valForm.situazione_familiare} onChange={e => setValForm(f => ({ ...f, situazione_familiare: e.target.value }))} rows={2} /></div>
              <div><Label className="text-xs">Situazione Economica</Label><DictableTextarea value={valForm.situazione_economica} onChange={e => setValForm(f => ({ ...f, situazione_economica: e.target.value }))} rows={2} /></div>
            </div>
            <div><Label className="text-xs">Servizi Attivi</Label><Input value={valForm.servizi_attivi} onChange={e => setValForm(f => ({ ...f, servizi_attivi: e.target.value }))} placeholder="ADI, SAD, Centro Diurno..." /></div>
            <Separator />
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Esito Valutazione</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Esito *</Label>
                <Select value={valForm.esito} onValueChange={v => setValForm(f => ({ ...f, esito: v }))}><SelectTrigger data-testid="val-esito"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="idoneo">Idoneo</SelectItem><SelectItem value="idoneo_con_riserva">Idoneo con Riserva</SelectItem><SelectItem value="non_idoneo">Non Idoneo</SelectItem><SelectItem value="da_rivalutare">Da Rivalutare</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs">Data Valutazione</Label><DatePickerField value={valForm.data_valutazione} onChange={v => setValForm(f => ({ ...f, data_valutazione: v }))} /></div>
            </div>
            <div><Label className="text-xs">Motivazione Esito</Label><DictableTextarea value={valForm.motivazione_esito} onChange={e => setValForm(f => ({ ...f, motivazione_esito: e.target.value }))} rows={2} /></div>
            <div><Label className="text-xs">Equipe Valutante</Label><Input value={valForm.equipe_valutante} onChange={e => setValForm(f => ({ ...f, equipe_valutante: e.target.value }))} placeholder="Medico, Infermiere, Assistente Sociale..." /></div>
            <div><Label className="text-xs">Note</Label><DictableTextarea value={valForm.note} onChange={e => setValForm(f => ({ ...f, note: e.target.value }))} rows={2} /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setShowValutazione(false)}>Annulla</Button><Button data-testid="save-valutazione" onClick={handleCreateValutazione} className="bg-emerald-600 hover:bg-emerald-700 text-white">Registra Valutazione</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* == DETTAGLIO CANDIDATO DIALOG == */}
      <Dialog open={showDettaglio} onOpenChange={setShowDettaglio}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Dettaglio - {selectedItem?.cognome} {selectedItem?.nome}</DialogTitle></DialogHeader>
          {selectedItem && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-xs text-slate-400 block">Data Nascita</span>{selectedItem.data_nascita ? new Date(selectedItem.data_nascita).toLocaleDateString('it-IT') : '-'}</div>
                <div><span className="text-xs text-slate-400 block">CF</span>{selectedItem.codice_fiscale || '-'}</div>
                <div><span className="text-xs text-slate-400 block">Telefono</span>{selectedItem.telefono}</div>
                <div><span className="text-xs text-slate-400 block">Email</span>{selectedItem.email || '-'}</div>
                <div><span className="text-xs text-slate-400 block">Referente</span>{selectedItem.referente_nome || '-'} {selectedItem.referente_telefono && `(${selectedItem.referente_telefono})`}</div>
                <div><span className="text-xs text-slate-400 block">Provenienza</span>{selectedItem.provenienza || '-'}</div>
                <div><span className="text-xs text-slate-400 block">Tipologia Assistenza</span>{tipAssistenza[selectedItem.tipologia_assistenza] || '-'}</div>
                <div><span className="text-xs text-slate-400 block">Data Richiesta</span>{selectedItem.data_richiesta ? new Date(selectedItem.data_richiesta).toLocaleDateString('it-IT') : '-'}</div>
              </div>
              {selectedItem.motivo_richiesta && <div><span className="text-xs text-slate-400 block">Motivo Richiesta</span><p className="text-sm">{selectedItem.motivo_richiesta}</p></div>}
              {selectedItem.note && <div><span className="text-xs text-slate-400 block">Note</span><p className="text-sm">{selectedItem.note}</p></div>}
              <Separator />
              <h4 className="text-sm font-semibold flex items-center gap-2"><Stethoscope className="w-4 h-4" />Valutazioni Idoneita</h4>
              {valutazioni.length === 0 ? <p className="text-xs text-slate-400">Nessuna valutazione effettuata</p> : (
                <div className="space-y-3">
                  {valutazioni.map(v => (
                    <Card key={v.id}>
                      <CardContent className="p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <Badge className={v.esito === 'idoneo' ? 'bg-emerald-100 text-emerald-700' : v.esito === 'non_idoneo' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>{v.esito?.replace(/_/g, ' ')}</Badge>
                          <span className="text-xs text-slate-400">{new Date(v.data_valutazione).toLocaleDateString('it-IT')} - {v.created_by_name}</span>
                        </div>
                        <p className="text-xs"><span className="font-medium">Diagnosi:</span> {v.diagnosi_principali}</p>
                        <div className="flex gap-2 flex-wrap">
                          {v.barthel_index != null && <Badge variant="outline" className="text-[10px]">Barthel: {v.barthel_index}</Badge>}
                          {v.mmse != null && <Badge variant="outline" className="text-[10px]">MMSE: {v.mmse}</Badge>}
                          {v.braden != null && <Badge variant="outline" className="text-[10px]">Braden: {v.braden}</Badge>}
                          {v.tinetti != null && <Badge variant="outline" className="text-[10px]">Tinetti: {v.tinetti}</Badge>}
                        </div>
                        {v.motivazione_esito && <p className="text-xs text-slate-500"><span className="font-medium">Motivazione:</span> {v.motivazione_esito}</p>}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* == AMMISSIONE DIALOG == */}
      <Dialog open={showAmmissione} onOpenChange={setShowAmmissione}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-emerald-600" />Ammissione Ospite</DialogTitle></DialogHeader>
          {ammItem && (
            <div className="space-y-4">
              <div className="bg-emerald-50 rounded-lg p-3">
                <p className="font-semibold text-sm text-slate-800">{ammItem.cognome} {ammItem.nome}</p>
                <p className="text-xs text-slate-500">{ammItem.codice_fiscale || ''} {ammItem.data_nascita ? `- Nato/a: ${new Date(ammItem.data_nascita).toLocaleDateString('it-IT')}` : ''}</p>
                {ammItem.retta_mensile > 0 && <p className="text-xs text-emerald-600 font-medium mt-1">Retta: EUR {ammItem.retta_mensile.toFixed(2)}/mese</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Stanza *</Label><Input value={ammForm.stanza} onChange={e => setAmmForm(f => ({ ...f, stanza: e.target.value }))} placeholder="Es. 12" data-testid="amm-stanza" /></div>
                <div><Label className="text-xs">Letto</Label><Input value={ammForm.letto} onChange={e => setAmmForm(f => ({ ...f, letto: e.target.value }))} placeholder="Es. A" data-testid="amm-letto" /></div>
                <div><Label className="text-xs">Area</Label>
                  <Select value={ammForm.area || '_none'} onValueChange={v => setAmmForm(f => ({ ...f, area: v === '_none' ? '' : v }))}>
                    <SelectTrigger><SelectValue placeholder="Nessuna preferenza" /></SelectTrigger>
                    <SelectContent><SelectItem value="_none">Nessuna preferenza</SelectItem><SelectItem value="verde">Area Verde</SelectItem><SelectItem value="gialla">Area Gialla</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Data Ingresso</Label><DatePickerField value={ammForm.data_ingresso} onChange={v => setAmmForm(f => ({ ...f, data_ingresso: v }))} testId="amm-data-ingresso" /></div>
              </div>
              <div><Label className="text-xs">Medico di Base (MMG)</Label><Input value={ammForm.mmg_nome} onChange={e => setAmmForm(f => ({ ...f, mmg_nome: e.target.value }))} placeholder="Es. Dott. Rossi" /></div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowAmmissione(false)}>Annulla</Button>
                <Button onClick={handleAmmetti} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="conferma-ammissione">
                  <UserPlus className="w-4 h-4 mr-1" />Conferma Ammissione
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
